import { useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { spacing, sizing, display, flexbox, } from '@material-ui/system';
import styled from 'styled-components';
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import { DatePicker, toISOString } from "../../components/date_picker"
import { getUUID,getPreferredName } from "../../utils/auth"

const Box = styled.div`${spacing}${sizing}${display}${flexbox}`;

import { axiosAuthedInstance } from "../../utils/axiosApi";
import { BaseContainer } from "../../components/container";
import Loading from "../../components/loading";
import SuccessDialog from "../../components/dialogs/success_dialog";
import ErrorDialog from "../../components/dialogs/error_dialog";
import {validateName, validateEmail, validateHKID } from '../../components/text_input'

const useStyles = makeStyles((theme) => ({
    heading: {
        fontWeight: 600,
        fontSize: theme.typography.fontSize * 1.5,
        marginBottom: theme.spacing(2)
    },
    share_text: {
        marginBottom: theme.spacing(2)
    },
    secondary_text: {
        fontSize: '0.8rem',
        lineHeight: '0.9rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1)
    },
    selector: {
        minWidth: 300,
    },
    select: {
        '&:focus': {
            backgroundColor: theme.palette.common.white
        }
    }
}))

const TestCreatePupil = (props) => {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [schools, setSchools] = useState([]);
    const [openSelect, setOpenSelect] = useState(false);
    const [openSelect2, setOpenSelect2] = useState(false);
    const [recipientUuid, setRecipientUuid] = useState(getUUID())
    const [school_id, setSchoolId] = useState('')
    const [state, setState] = useState({full_name: '', hkid: '', system_id: '', year: '', school_class: ''})
    const [formErrors, setFormErrors] = useState({full_name: '', hkid: '', system_id: '', year: '', school_class: ''})
    const [dob, handleDateChange] = useState(null)
    const [dobError, setDobError] = useState('')
    const [success, setSuccess] = useState('')
    const [showSuccess, setShowSuccess] = useState(false)
    const [error, setError] = useState('')
    const [showError, setShowError] = useState(false)
    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState('')

    const classes = useStyles();

    function handleError (error, defaultMsg) {
        let msg = defaultMsg
        if (error.response?.data?.error) {
            console.error(error)
            msg = error.response.data.error
        }
        setError(msg)
        setShowError(true)
        setLoading(false)
    }

    async function createPupil() {
        try {
            setLoading(true)
            await axiosAuthedInstance.post('/test/create-school-pupil/', {
                school_id,
                data: {...state, dob: toISOString(dob)},
                uuid: recipientUuid}
            )
            setLoading(false)
            setSuccess('Pupil created successfully')
            setShowSuccess(true)
        } catch (error) {
            handleError(error, 'Error creating pupil')
        }
    }

    async function getSchools() {
        try {
            setLoading(true)
            const response = await axiosAuthedInstance.get('/test/get-schools/')
            setSchools(response.data.data)
            setSchoolId(response.data.data[0].uuid)
            setLoading(false)
        } catch (error) {
            handleError(error, 'Error getting schools')
        }
    }

    async function getUsers() {
        try {
            setLoading(true)
            const response = await axiosAuthedInstance.get('/test/get-other-users/');
            setUsers(response.data.data)
            setLoading(false);
        } catch (error) {
            handleError(error, 'Error getting users')
        }
    }

    const sendEmail = () => {
        setLoading(true)
        axiosAuthedInstance.post('/test/send-school-invite/', {
            email,
            school_id,
            data: {...state, dob: toISOString(dob)},
        }).then(() => {
            setLoading(false)
            setSuccess('Email sent successfully')
            setShowSuccess(true)
        }).catch(err => {
            handleError(err, 'Error sending email.')
        })
    }

    function handleChange(event) {
        let error
        let text = event.target.value
        switch (event.target.name) {
            case 'hkid':
                text = text.toUpperCase()
                error = validateHKID(text)
                break;
            case 'full_name':
                error = validateName(text)
                break;
            default:
                break;
        }
        setFormErrors(prevState => ({...prevState, [event.target.name]: error}))
        setState(prevState => ({...prevState, [event.target.name]: text}))
    }

    const handleEmailChange = (event) => {
        setEmail(event.target.value)
        setEmailError(validateEmail(event.target.value))
    }

    useEffect(() => {
        getSchools();
        getUsers();
    }, []);

    return (
        <BaseContainer {...props}>
            {loading && <Loading loading={loading} />}
            <Typography variant="h5" className={classes.heading}>
                Create new pupil
            </Typography>
            <Typography className={classes.share_text}>Select User</Typography>
            <ClickAwayListener onClickAway={() => setOpenSelect(false)}>
                <FormControl className={classes.selector} variant="outlined" >
                    <InputLabel>Recipient</InputLabel>
                    <Select
                        classes={{ select: classes.select }}
                        open={openSelect}
                        onOpen={() => setOpenSelect(true)}
                        onClose={() => setOpenSelect(false)}
                        native
                        value={recipientUuid}
                        onChange={(event) => {
                            setRecipientUuid(event.target.value)
                        }}
                        label="Recipient">
                        <option key='me' value={getUUID()}>{`${getPreferredName()} (Me)`}</option>
                        {users.map((user, i) => <option key={i} value={user.uuid}>{user.full_name}</option>)}
                    </Select>
                </FormControl>
            </ClickAwayListener>
            <Typography className={classes.share_text}>Select School</Typography>
            <ClickAwayListener onClickAway={() => setOpenSelect2(false)}>
                <FormControl className={classes.selector} variant="outlined" >
                    <InputLabel>School</InputLabel>
                    <Select
                        classes={{ select: classes.select }}
                        open={openSelect2}
                        onOpen={() => setOpenSelect2(true)}
                        onClose={() => setOpenSelect2(false)}
                        native
                        value={school_id}
                        onChange={(event) => {
                            setSchoolId(event.target.value)
                        }}
                        label="School">
                        {schools.map((school, i) => <option key={i} value={school.uuid}>{school.identifier}</option>)}
                    </Select>
                </FormControl>
            </ClickAwayListener>
            <TextField
                id="full_name"
                name="full_name"
                label="Full Name"
                placeholder="Full Name"
                fullWidth
                error={!!formErrors.full_name}
                helperText={formErrors.full_name}
                margin="normal"
                value={state.full_name}
                onChange={handleChange}
            />
            <TextField
                id="system_id"
                name="system_id"
                label="System ID"
                placeholder="System ID"
                fullWidth
                error={!!formErrors.system_id}
                helperText={formErrors.system_id}
                margin="normal"
                value={state.system_id}
                onChange={handleChange}
            />
            <TextField
                id="year"
                name="year"
                label="Year"
                placeholder="Year"
                fullWidth
                error={!!formErrors.year}
                helperText={formErrors.year}
                margin="normal"
                value={state.year}
                onChange={handleChange}
                inputProps={{
                    maxLength: 10,
                }}
            />
            <TextField
                id="school_class"
                name="school_class"
                label="School class"
                placeholder="School class"
                fullWidth
                error={!!formErrors.school_class}
                helperText={formErrors.school_class}
                margin="normal"
                value={state.school_class}
                onChange={handleChange}
                inputProps={{
                    maxLength: 50,
                }}
            />
            <DatePicker
                disableFuture
                fullWidth
                id="dob"
                name="dob"
                openTo="year"
                label="Date of birth"
                views={["year", "month", "date"]}
                placeholder={'02/10/2010'}
                value={dob}
                disabled={loading}
                onError={(err) => setDobError(err)}
                onChange={handleDateChange}
            />
            <TextField
                id="hkid"
                name="hkid"
                label="HKID (optional)"
                placeholder="C6886880"
                fullWidth
                error={!!formErrors.hkid}
                helperText={formErrors.hkid}
                margin="normal"
                value={state.hkid}
                onChange={handleChange}
                inputProps={{
                    maxLength: 9,
                }}
            />
            <Box width="100%" mt={2} mb={2}>
                <Button fullWidth variant='contained' color='primary' onClick={() => createPupil()}>
                    Send to User
                </Button>
            </Box>
            <Typography>
                Or send an email
            </Typography>
            <TextField
                id="email"
                name="email"
                label="Email"
                placeholder="Enter recipient's email address"
                fullWidth
                margin="normal"
                value={email}
                error={!!emailError}
                helperText={emailError}
                onChange={handleEmailChange}
            />
            <Box width="100%" mt={2} mb={2}>
                <Button fullWidth variant='contained' color='primary' onClick={() => sendEmail()}>
                    Send Email
                </Button>
            </Box>
            <SuccessDialog open={showSuccess} setOpen={setShowSuccess} success={success}/>
            <ErrorDialog open={showError} setOpen={setShowError} error={error}/>
        </BaseContainer>
    )
}
export default TestCreatePupil;
